import { Directive, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ResizeObserverService } from '@frontend/sports/common/core/utils/dom';
import { Subject, asapScheduler, debounceTime, distinctUntilChanged, filter, map, merge, tap } from 'rxjs';

@Directive()
export abstract class BaseOptionRendererDirective implements OnChanges {
    private element = inject(ElementRef);
    private readonly resizeObserver = inject(ResizeObserverService);

    @Output() readonly rerender = new EventEmitter<number>();
    @Input() optionPickClass: string;
    @Input() maxColumns = 3;
    @Input() tolerance = 25;
    @Input() resetClass = false;
    @Input() responsiveColumns = 0;

    columnSettings = { maxColumns: this.maxColumns, oddColumns: false };
    elWidth = 0;

    private readonly optionGroupOrResponsiveColumnChange$ = new Subject<void>();

    constructor() {
        this.manageResizeEvents();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const optionGroupChanges = changes.optionGroup;

        if (optionGroupChanges && optionGroupChanges.currentValue) {
            // Update maxColumns and oddColumns on optionGroup change.
            this.columnSettings = this.getColumnsSettings();
        }
        if ((optionGroupChanges && !optionGroupChanges.firstChange) || changes.responsiveColumns) {
            this.optionGroupOrResponsiveColumnChange$.next();
        }
    }

    protected abstract getColumnsSettings(): { maxColumns: number; oddColumns: boolean };

    private manageResizeEvents(): void {
        merge(
            this.resizeObserver.observe(this.element).pipe(
                map((event) => event.contentRect.width),
                distinctUntilChanged(),
                tap((width) => (this.elWidth = width)),
                filter(() => !this.responsiveColumns),
            ),
            this.optionGroupOrResponsiveColumnChange$,
        )
            .pipe(
                filter((el) => el !== 0),
                debounceTime(0, asapScheduler),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                this.renderColumns();
            });
    }

    /**
     * Toggle a class of the parent component.
     *
     * @param className - Class name
     * @param toggle - true to add , false to remove
     */
    private toggleClass(className: string, toggle: boolean): void {
        this.element.nativeElement.classList.toggle(className, toggle);
    }

    private resetClasses(element: HTMLElement): void {
        element.classList.remove('single', 'double', 'triple', 'quadruple');
    }

    private renderColumns(): void {
        const element = this.element.nativeElement as HTMLElement;
        const elementWidth = this.elWidth;

        if (this.responsiveColumns !== 0) {
            this.toggleClass('single', this.responsiveColumns === 1);
            this.toggleClass('double', this.responsiveColumns === 2);
            this.rerender.next(this.responsiveColumns);
        } else {
            this.resetClass && this.resetClasses(element);
            const options = element.querySelectorAll(this.optionPickClass);

            const longest = Array.from(options)
                .map((option) => this.width(option))
                .sort((first, second) => first - second)
                .pop();

            const width = longest! + this.tolerance;
            const fittingColumns = Math.floor(elementWidth / width);

            let columns = Math.min(options.length, fittingColumns, this.columnSettings.maxColumns);

            if (columns === 2 && this.columnSettings.oddColumns) {
                columns = 1;
            }

            if (columns < 1) {
                columns = 1;
            } else if (columns > this.maxColumns) {
                columns = this.maxColumns;
            }

            this.toggleClass('single', columns === 1);
            this.toggleClass('double', columns === 2);
            this.toggleClass('triple', columns === 3);
            this.toggleClass('quadruple', columns === 4);

            this.rerender.next(columns);
        }
    }

    private width(element: Element): number {
        if (!element || !element.children) {
            return 0;
        }

        return Array.from(element.children).reduce((total, current) => total + (current as HTMLElement).offsetWidth, 0);
    }
}
